import React from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  List,
  ListItem,
  Link,
} from "@chakra-ui/react";
import Header from "../components/Header";
import { pxToRem } from "../utils/editor.ts";

const TermsOfUse: React.FC = () => {
  return (
    <Box h="100%" padding={pxToRem(30)} pt={0}>
      {/* Decorative background elements */}
      <Box
        position="absolute"
        bottom="20%"
        right="10%"
        w={pxToRem(300)}
        h={pxToRem(300)}
        bg="rgba(29, 138, 255, 0.08)"
        borderRadius="full"
        filter={`blur(${pxToRem(80)})`}
        zIndex={0}
      />

      {/* Header with user menu */}
      <Header />

      <Box
        pl={pxToRem(60)}
        pt={pxToRem(20)}
        pb={pxToRem(40)}
        position="relative"
        zIndex={1}
        color={"#000"}
      >
        <VStack align="flex-start">
          <Heading mb={0} as="h1" size="xl">
            Librum Term of Use
          </Heading>
          <Text fontSize={"14px"} color="#000">
            Effective Date: 8/18/2025
            <span style={{ marginLeft: pxToRem(80) }}></span>
            Last Updated: 8/18/2025
          </Text>
          <Box overflowY={"auto"} h={"65vh"}>
            <Heading mb={0} as="h2" size="md" pt={pxToRem(10)}>
              Introduction
            </Heading>
            <Text>
              Doenba.ai website and its products/services are provided by Librum
              Technologies ("Librum"). These Terms of Use ("Terms") govern your
              access to and use of the Doenba.ai website, mobile applications,
              and related services (collectively, the "Services").
            </Text>
            <Text>
              By creating an account, purchasing a subscription, or otherwise
              using the Services, you agree to be bound by these Terms. If you
              do not agree, you must stop using the Services immediately.
            </Text>

            <Heading mb={0} as="h2" size="md" pt={pxToRem(10)}>
              Eligibility
            </Heading>
            <List styleType="decimal" pl={pxToRem(20)}>
              <ListItem>
                You must be 18 years or older or have parental/guardian consent
                to use the Services.
              </ListItem>
              <ListItem>
                You must have the legal authority to enter into binding
                agreements in your jurisdiction.
              </ListItem>
              <ListItem>
                If you use the Services on behalf of an organization, you
                confirm you are authorized to bind that organization to these
                Terms.
              </ListItem>
            </List>

            <Heading mb={0} as="h2" size="md" pt={pxToRem(10)}>
              Account Registration
            </Heading>
            <List styleType="decimal" pl={pxToRem(20)}>
              <ListItem>
                You must provide accurate, complete, and current information
                when creating an account.
              </ListItem>
              <ListItem>
                You are solely responsible for maintaining the confidentiality
                of your login credentials and for all activities under your
                account.
              </ListItem>
              <ListItem>
                Notify Librum immediately if you suspect unauthorized access or
                security breaches.
              </ListItem>
            </List>

            <Heading mb={0} as="h2" size="md" pt={pxToRem(10)}>
              Acceptable Use
            </Heading>
            <Text>
              The Services are for personal and/or educational uses only and not
              to be used for commercial purposes without express permission.
            </Text>
            <Text>You agree not to:</Text>
            <List styleType="decimal" pl={pxToRem(20)}>
              <ListItem>
                Reverse-engineer, copy, or modify the Services or any underlying
                software.
              </ListItem>
              <ListItem>
                Use the Services to create competing products or services.
              </ListItem>
              <ListItem>
                Upload, post, or share unlawful, harmful, infringing, or
                otherwise objectionable content.
              </ListItem>
              <ListItem>
                Use the Services in violation of applicable laws, including data
                protection and intellectual property laws.
              </ListItem>
              <ListItem>
                We reserve the right to suspend or terminate your account for
                violations of this section.
              </ListItem>
            </List>

            <Heading mb={0} as="h2" size="md" pt={pxToRem(10)}>
              AI-Generated Content Disclaimer
            </Heading>
            <Text>
              Librum uses artificial intelligence to generate educational
              materials, suggestions, and other outputs.
            </Text>
            <Text>
              AI-generated content is not guaranteed to be accurate, complete,
              or suitable for any specific purpose.
            </Text>
            <Text>
              You are responsible for verifying AI outputs before relying on
              them for academic, legal, medical, or other important decisions.
            </Text>

            <Heading mb={0} as="h2" size="md" pt={pxToRem(10)}>
              Intellectual Property
            </Heading>
            <Text>
              All code, text, graphics, interfaces, and trademarks related to
              Librum are owned or licensed by Librum and protected by applicable
              laws.
            </Text>
            <Text>
              You retain rights to any content you upload but grant Librum a
              non-exclusive, worldwide, royalty-free license to store, process,
              and display it as needed to provide the Services.
            </Text>
            <Text>
              You may not use Librum's trademarks or branding without prior
              written consent.
            </Text>

            <Heading mb={0} as="h2" size="md" pt={pxToRem(10)}>
              Payment, Billing & Renewals
            </Heading>
            <Text>
              Paid Services require a valid payment method at the time of
              purchase.
            </Text>
            <Text>
              Subscriptions automatically renew at the end of each billing cycle
              unless canceled before the renewal date.
            </Text>
            <Text>
              Prices may change, and we will notify you of any material changes
              in advance.
            </Text>

            <Heading mb={0} as="h2" size="md" pt={pxToRem(10)}>
              Free Trials
            </Heading>
            <Text>
              Free trials, if offered, are limited-time offers for new users.
            </Text>
            <Text>
              If you do not cancel before the trial ends, your payment method
              will be charged for the selected subscription plan.
            </Text>

            <Heading mb={0} as="h2" size="md" pt={pxToRem(10)}>
              Cancellation by you
            </Heading>
            <Text>
              You may cancel at any time through your account dashboard.
            </Text>
            <Text>
              Your subscription will remain active until the end of the current
              billing period—no mid-cycle refunds unless covered under our
              Refund Policy.
            </Text>

            <Heading mb={0} as="h2" size="md" pt={pxToRem(10)}>
              Cancellation or Suspension by Librum
            </Heading>
            <List styleType="decimal" pl={pxToRem(20)}>
              <ListItem>
                We may suspend or terminate your account without notice if:
              </ListItem>
              <ListItem>You violate these Terms.</ListItem>
              <ListItem>
                We are required by law or regulatory authorities.
              </ListItem>
              <ListItem>There are technical or security concerns.</ListItem>
            </List>
            <Text>
              We will attempt to notify you and, if applicable, allow you to
              retrieve stored content before termination.
            </Text>

            <Heading mb={0} as="h2" size="md" pt={pxToRem(10)}>
              Limitation of Liability
            </Heading>
            <Text>To the maximum extent permitted by law:</Text>
            <List styleType="decimal" pl={pxToRem(20)}>
              <ListItem>
                Librum is not liable for any indirect, incidental, special, or
                consequential damages, including loss of data, profits, or
                reputation.
              </ListItem>
              <ListItem>
                Our total liability for all claims shall not exceed the amount
                you paid Librum in the 12 months preceding the claim.
              </ListItem>
            </List>

            <Heading mb={0} as="h2" size="md" pt={pxToRem(10)}>
              Governing Law
            </Heading>
            <Text>
              These Terms are governed by the laws of [Insert Jurisdiction],
              without regard to its conflict of law principles.
            </Text>

            <Heading mb={0} as="h2" size="md" pt={pxToRem(10)}>
              Changes to Terms
            </Heading>
            <Text>
              We may update these Terms from time to time. Continued use of the
              Services after updates constitutes your acceptance of the revised
              Terms.
            </Text>
            <Link color="blue.500" href="#">
              Librum - Refund Policy
            </Link>

            <Text pt={pxToRem(10)}>
              Contact:
              <Link color="blue.500" href="mailto:support@doenba.ai">
                support@doenba.ai
              </Link>
            </Text>
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};

export default TermsOfUse;
